import { TFunction, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Form, Select, Modal, Tag } from 'antd';
import { AppDispatch } from '../../../redux/store';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import FormItem from '../../../components/form/FormItem';
import { inviteTestUsers } from '../../../redux/reducers/testuserSlice';
import { InviteTestUserCommand } from '../../../api';
import OmniNotification from 'components/OmniNotification';

const getValidationSchema = (t: TFunction) =>
	yup.object().shape({
		invitationEmails: yup
			.array()
			// eslint-disable-next-line
			.of(
				yup
					.string()
					.email('${value} must be a valid email')
					.max(
						254,
						t('validation.maxLength', {
							fieldName: t('playersPage.Tab.Users.InviteModal.Email'),
							count: 254,
						}),
					),
			)
			.min(
				1,
				t('validation.min', {
					fieldName: t('playersPage.Tab.Users.InviteModal.Email'),
					min: 1,
				}),
			),
	});

const emailSchema = yup.object().shape({
	email: yup.string().email().max(254),
});

const emailValidation = (email: string) => {
	return emailSchema.isValidSync({ email });
};

interface Props {
	visible: boolean;
	setVisible: (visible: boolean) => void;
	afterSubmit: () => void;
	projectId: number;
}

interface FormValues {
	invitationEmails: string[];
}

export default function InviteModal(props: Props) {
	const { visible, setVisible } = props;
	const { t } = useTranslation();
	const dispatch: AppDispatch = useDispatch();
	const [confirmInviteModalLoading, setConfirmInviteModalLoading] =
		useState(false);
	const validationSchema = useMemo(() => getValidationSchema(t), [t]);
	const formSubmitButton = useRef();
	const {
		control,
		handleSubmit,
		setError,
		trigger,
		setFocus,
		watch,
		formState,
		reset,
		setValue,
	} = useForm<FormValues>({
		mode: 'all',
		resolver: yupResolver(validationSchema),
		defaultValues: {
			invitationEmails: [],
		},
	});

	useEffect(() => reset(), [visible]);

	const submitInvitation = async (values: FormValues) => {
		setConfirmInviteModalLoading(true);
		const invitation: InviteTestUserCommand = {
			projectId: props.projectId,
			callbackUrl: `${window.location.origin}/invitation`,
			testUserEmails: values.invitationEmails,
		};

		const result = await dispatch(inviteTestUsers(invitation));

		if (inviteTestUsers.fulfilled.match(result)) {
			let totalAdded = values.invitationEmails.length;
			if (result.payload.length > 0) {
				totalAdded = totalAdded - result.payload.length;
				OmniNotification.info({
					message: t('playersPage.Tab.Users.InviteModal.Info.Header', {
						count: result.payload.length,
					}),
					description: t('playersPage.Tab.Users.InviteModal.Info.Body', {
						//@ts-ignore
						usersList: result.payload.join(', '),
					}),
				});
			}
			if (totalAdded > 0) {
				OmniNotification.success({
					message: t('playersPage.Tab.Users.InviteModal.Success.Header', {
						emails: totalAdded,
					}),
					description: t('playersPage.Tab.Users.InviteModal.Success.Body', {
						emails: totalAdded,
					}),
				});
			}
		} else {
			OmniNotification.error({
				message: t('notification.error.title'),
				description: t('api.defaultError'),
			});
		}
		setConfirmInviteModalLoading(false);
		props.afterSubmit();
	};

	const tagRender = (props: any) => {
		const { label, value, closable, onClose } = props;
		const onPreventMouseDown = (event: React.MouseEvent<HTMLSpanElement>) => {
			event.preventDefault();
			event.stopPropagation();
		};
		const color = emailValidation(value) ? undefined : 'red';
		return (
			<Tag
				color={color}
				onMouseDown={onPreventMouseDown}
				closable={closable}
				onClose={onClose}
				style={{
					marginRight: 3,
					maxWidth: '100%',
					overflow: 'hidden',
					whiteSpace: 'break-spaces',
				}}
			>
				{label}
			</Tag>
		);
	};

	return (
		<>
			{visible && (
				<Modal
					title={<span>{t('playersPage.Tab.Users.InviteModal.Header')}</span>}
					visible
					centered
					okText={t('playersPage.Tab.Users.InviteModal.OkButton')}
					cancelText={t('common.cancel')}
					onOk={handleSubmit(submitInvitation)}
					onCancel={() => setVisible(false)}
					cancelButtonProps={{
						className: 'color-dark',
					}}
					okButtonProps={
						{
							type: 'primary',
							htmlType: 'submit',
							disabled: !formState.isValid,
							'data-testid': 'testuser-invite-modal-submit-button',
						} as any
					}
					confirmLoading={confirmInviteModalLoading}
					maskClosable={false}
					keyboard={false}
				>
					<>
						<Form data-testid="form" layout="vertical">
							<FormItem
								name="invitationEmails"
								label={t('playersPage.Tab.Users.InviteModal.Email')}
								control={control}
								hint={t('playersPage.Tab.Users.InviteModal.Email.hint')}
								render={({ field }) => (
									<>
										<Select
											{...field}
											open={false}
											mode="tags"
											style={{ width: '100%' }}
											tokenSeparators={[',', ' ']}
											tagRender={tagRender}
											data-testid="invitationEmails"
										/>
									</>
								)}
							/>
						</Form>
					</>
				</Modal>
			)}
		</>
	);
}
